// import type { FC, KeyboardEvent, ChangeEvent } from "react";
// import { useCallback, useId } from "react";

// type IntegerInputProps = {
//   max?: number;
//   value: number;
//   onChange: (value: number) => void;
// };

// export default function IntegerInput({
//   max = 999,
//   value,
//   onChange,
// }: IntegerInputProps) {
//   const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
//     const newValue = parseInt(e.target.value, 10);
//     if (!isNaN(newValue) && newValue >= 0 && newValue <= max) {
//       onChange(newValue);
//     } else if (e.target.value === "") {
//       onChange(0); // Reset to 0 if input is cleared
//     }
//   };

//   const handleDecrement = () => {
//     const newValue = value - 1;
//     if (newValue >= 0) {
//       onChange(newValue);
//     }
//   };

//   const handleIncrement = () => {
//     const newValue = value + 1;
//     if (newValue <= max) {
//       onChange(newValue);
//     }
//   };

//   return (
//     <div>
//       <div className="relative flex items-center justify-center w-30 md:w-36">
//         <button
//           onClick={handleDecrement}
//           className="hover:bg-gray-600 rounded-s-lg p-2 md:p-3 h-11 focus:ring-gray-700 focus:ring-2 focus:outline-none bg-brand-neutral-650 mr-1"
//         >
//           <svg
//             className="w-3 h-3 text-white"
//             aria-hidden="true"
//             xmlns="http://www.w3.org/2000/svg"
//             fill="none"
//             viewBox="0 0 18 2"
//           >
//             <path
//               stroke="currentColor"
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth="2"
//               d="M1 1h16"
//             />
//           </svg>
//         </button>
//         <input
//           type="text"
//           pattern="(?:0|[1-9]\d*)"
//           inputMode="decimal"
//           autoComplete="off"
//           value={value}
//           onChange={handleChange}
//           className="appearance-none bg-brand-neutral-650 border-x-0 h-11 text-center text-sm block w-full py-2.5 placeholder-gray-400 text-white focus:ring-blue-500 focus:border-blue-500"
//         />
//         <button
//           type="button"
//           onClick={handleIncrement}
//           className="hover:bg-gray-600 bg-brand-neutral-650 ml-1 rounded-e-lg p-3 h-11 focus:ring-gray-700 focus:ring-2 focus:outline-none"
//         >
//           <svg
//             className="w-3 h-3 text-white"
//             aria-hidden="true"
//             xmlns="http://www.w3.org/2000/svg"
//             fill="none"
//             viewBox="0 0 18 18"
//           >
//             <path
//               stroke="currentColor"
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth="2"
//               d="M9 1v16M1 9h16"
//             />
//           </svg>
//         </button>
//       </div>
//     </div>
//   );
// }

import { cn } from "@/util/cn";
import { useState, ChangeEvent } from "react";
import { Input } from "./input";

type IntegerInputProps = {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  setValue: (value: number) => void;
  buttonClassName?: string;
  inputClassName?: string;
  className?: string;
};

export default function IntegerInput({
  min = 0,
  max = 999,
  step = 1,
  value = min,
  setValue,
  buttonClassName,
  inputClassName,
  className,
}: IntegerInputProps) {
  const [inputValue, setInputValue] = useState<string>(value.toString());

  // Regex: only allow digits (optional empty string for editing)
  const regex = /^\d*$/;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    if (newValue === "") {
      setInputValue("");
      setValue(0);
    } else if (regex.test(newValue)) {
      const parsedValue = parseInt(newValue, 10);
      if (min <= parsedValue && parsedValue <= max) {
        setInputValue(newValue);
        setValue(parsedValue);
      }
    }
  };

  const handleIncrement = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.preventDefault();
    const parsedValue = parseInt(inputValue, 10);
    const numericValue = isNaN(parsedValue) ? 0 : parsedValue;
    const newValue = numericValue + step;
    if (newValue <= max) {
      setInputValue(newValue.toString());
      setValue(newValue);
    }
  };

  const handleDecrement = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.preventDefault();
    const parsedValue = parseInt(inputValue, 10);
    const numericValue = isNaN(parsedValue) ? 0 : parsedValue;
    const newValue = numericValue - step;
    if (newValue >= min) {
      setInputValue(newValue.toString());
      setValue(newValue);
    }
  };

  return (
    <div
      className={cn(
        "relative flex items-center justify-center w-16 sm:w-30 md:w-36",
        className
      )}
    >
      <button
        disabled={inputValue === min.toString()}
        onClick={(e) => handleDecrement(e)}
        className={cn(
          "hover:bg-gray-600 rounded-s-lg p-3 h-11 focus:ring-gray-700 focus:ring-2 focus:outline-none bg-brand-neutral-650 mr-1 hidden sm:block cursor-pointer disabled:cursor-default",
          buttonClassName
        )}
      >
        <svg
          className="w-3 h-3 text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 2"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M1 1h16"
          />
        </svg>
      </button>
      <Input
        value={inputValue}
        onChange={handleChange}
        className={cn(
          "rounded-none border-transparent bg-brand-neutral-650 h-11 text-center text-sm block w-full py-2.5 placeholder-gray-400 text-white focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          inputClassName
        )}
      />
      <button
        type="button"
        disabled={inputValue === max.toString()}
        onClick={(e) => handleIncrement(e)}
        className={cn(
          "hover:bg-gray-600 bg-brand-neutral-650 ml-1 rounded-e-lg p-3 h-11 focus:ring-gray-700 focus:ring-2 focus:outline-none hidden sm:block cursor-pointer disabled:cursor-default",
          buttonClassName
        )}
      >
        <svg
          className="w-3 h-3 text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 18"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 1v16M1 9h16"
          />
        </svg>
      </button>
    </div>
  );
}
